import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useStandardAgentStore } from "@/store/standardAgentStore";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Paperclip,
  X,
  Pause,
  Clock,
  Store,
  Mail,
  Upload,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { SendButton } from "@/components/SendButton";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatRelative } from "date-fns";
import { useEmailSyncsStore } from "@/store/emailSyncsStore";
import { useConnectionsStore } from "@/store/connectionsStore";
import Layout from "@/components/layout";
import logoBlack from "@/assets/logo_mark_black.svg";
import logoWhite from "@/assets/logo_mark_white.svg";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { StandardAgentThreadView } from "./components/StandardAgentThreadView";
import { ThreadSearchBar } from "./ThreadSearchBar";
import { useStandardAgentDisplayName } from "./useStandardAgentDisplayName";
import { useDebounce } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import { fetchApi } from "@/lib/api";
import { Asset } from "@/store/quoteStore";
import { AssetPreviewDialog } from "@/components/AssetPreviewDialog";
import { captureException } from "@/lib/analytics";
import { VisibilityToggle } from "./components/VisibilityToggle";
import { useFeatureFlag } from "@/store/featureFlagStore";

import {
  AGENT_SDK_THREAD_STATUS,
  AgentSdkThread,
  AssetSchema,
  THREAD_VISIBILITY,
  type ThreadVisibility,
} from "@forgepoint/shared";
import { z } from "zod";

type ThreadTab = "my-threads" | "org-threads";
const MICROSOFT_OUTLOOK_APP_ID = "microsoft_outlook";

const UploadResponseSchema = z.object({
  uploadUrl: z.string(),
  asset: AssetSchema,
});

const mapUploadedAsset = (asset: z.infer<typeof AssetSchema>): Asset => ({
  id: asset.id,
  externalUrl: asset.externalUrl,
  fileName: asset.fileName,
  fileSize: asset.fileSize,
  fileType: asset.fileType,
  createdAt: asset.createdAt.toISOString(),
});

/**
 * Returns the status icon for a thread, taking into account pending tasks
 * and active wait timers in addition to the thread status.
 */
const getStatusIcon = (thread: AgentSdkThread) => {
  if (thread.status === AGENT_SDK_THREAD_STATUS.ACTIVE) {
    return <Loader2 className="h-4 w-4 text-info animate-spin" />;
  }
  if (thread.status === AGENT_SDK_THREAD_STATUS.FAILED) {
    return <XCircle className="h-4 w-4 text-danger" />;
  }
  if (thread.pendingTaskCount && thread.pendingTaskCount > 0) {
    return <Pause className="h-4 w-4 text-warning" />;
  }
  if (thread.hasActiveWaitTimer) {
    return <Clock className="h-4 w-4 text-warning" />;
  }
  if (thread.status === AGENT_SDK_THREAD_STATUS.INTERRUPTED) {
    return <Pause className="h-4 w-4 text-warning" />;
  }
  if (thread.status === AGENT_SDK_THREAD_STATUS.COMPLETED) {
    return <CheckCircle className="h-4 w-4 text-success" />;
  }
  return <Clock className="h-4 w-4 text-muted-foreground" />;
};

/**
 * Orders threads by most recent activity (updatedAt) descending, createdAt as
 * a stable tiebreak. Mirrors the backend list order so the list reorders live
 * as threads are worked on — polling refreshes each thread's updatedAt in place
 * between full refetches, and re-sorting on read floats the active one to top.
 */
const sortThreadsByRecentActivity = (
  threads: AgentSdkThread[],
): AgentSdkThread[] =>
  [...threads].sort(
    (a, b) =>
      b.updatedAt.getTime() - a.updatedAt.getTime() ||
      b.createdAt.getTime() - a.createdAt.getTime(),
  );

export function StandardAgentChat() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    threads,
    isLoadingThreads,
    isSending,
    fetchThreads,
    createThread,
    stopAllPolling,
  } = useStandardAgentStore();

  const sortedThreads = useMemo(
    () => sortThreadsByRecentActivity(threads),
    [threads],
  );

  const [newMessage, setNewMessage] = useState("");
  const [attachedAssets, setAttachedAssets] = useState<Asset[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isIntegrationsOpen, setIsIntegrationsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ThreadTab>("my-threads");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [visibility, setVisibility] = useState<ThreadVisibility>(
    THREAD_VISIBILITY.ORGANIZATION,
  );
  const standardAgentName = useStandardAgentDisplayName();
  const user = useAuthStore((state) => state.user);
  const selectedThreadId = searchParams.get("threadId");
  const threadViewOpen = selectedThreadId !== null;

  const quickQuestions = [
    "What quotes are waiting on follow up?",
    "Which opportunities are at risk this week?",
    "Do I have any high priority approvals right now?",
    "Are any deals stalled?",
  ];

  const enableEmailSearch = useFeatureFlag("email-search");
  const { fetchEmailSyncs, getAllEmailSyncs } = useEmailSyncsStore();
  const { fetchConnectionsByApp, getConnectionsByApp } = useConnectionsStore();

  const emailSyncs = getAllEmailSyncs();
  const outlookConnections = getConnectionsByApp(MICROSOFT_OUTLOOK_APP_ID);

  const hasOutlookConnection =
    emailSyncs.length > 0 &&
    emailSyncs.some((sync) =>
      outlookConnections.some((conn) => conn.id === sync.connectionId),
    );
  const threadListUserId = activeTab === "my-threads" ? user?.id : undefined;
  const isWaitingForUserThreads =
    activeTab === "my-threads" && !threadListUserId;

  const handleTabChange = useCallback((tab: string) => {
    if (tab === "my-threads" || tab === "org-threads") {
      setActiveTab(tab);
    }
  }, []);

  useEffect(() => {
    if (isWaitingForUserThreads) {
      return;
    }

    fetchThreads({
      userId: threadListUserId,
      search: debouncedSearch,
    });
  }, [
    fetchThreads,
    isWaitingForUserThreads,
    threadListUserId,
    debouncedSearch,
  ]);

  useEffect(() => {
    return () => {
      stopAllPolling();
    };
  }, [stopAllPolling]);

  useEffect(() => {
    if (!enableEmailSearch) {
      return;
    }
    fetchEmailSyncs();
    fetchConnectionsByApp(MICROSOFT_OUTLOOK_APP_ID);
  }, [fetchEmailSyncs, fetchConnectionsByApp, enableEmailSearch]);

  const handleOutlookIntegrationClick = () => {
    setIsIntegrationsOpen(false);
    navigate("/settings?tab=email-integrations");
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending || isUploadingFile) {
      return;
    }

    const assetIds =
      attachedAssets.length > 0 ? attachedAssets.map((asset) => asset.id) : [];
    const result = await createThread(
      newMessage.trim(),
      undefined,
      assetIds.length > 0 ? assetIds : undefined,
      visibility,
    );
    if (result) {
      setNewMessage("");
      setAttachedAssets([]);
      setSearchParams({ threadId: result.threadId });
      if (!isWaitingForUserThreads) {
        await fetchThreads({
          userId: threadListUserId,
          search: debouncedSearch,
        });
      }
    } else {
      toast.error("Failed to start conversation");
    }
  };

  const handleThreadClick = (threadId: string) => {
    setSearchParams({ threadId });
  };

  /**
   * Uploads an array of files to asset storage and adds them to attachedAssets.
   * @param files - The files to upload
   */
  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    setIsUploadingFile(true);

    for (const file of files) {
      try {
        const response = await fetchApi("api/assets/upload", "POST", {
          body: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          },
        });

        if (!response.ok || !response.data) {
          continue;
        }

        const parsed = UploadResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          continue;
        }

        const data = parsed.data;

        await fetch(data.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        setAttachedAssets((prev) => [...prev, mapUploadedAsset(data.asset)]);
      } catch (error) {
        const uploadError =
          error instanceof Error ? error : new Error("Failed to upload file");
        captureException(uploadError);
      }
    }

    setIsUploadingFile(false);
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    await uploadFiles(Array.from(files));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Handles files dropped onto the chat input area.
   * @param event - The drag event from the drop zone
   */
  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (isSending || isUploadingFile) {
      return;
    }

    const files = Array.from(event.dataTransfer.files);
    await uploadFiles(files);
  };

  const handleRemoveAsset = (assetId: string) => {
    setAttachedAssets((prev) => prev.filter((asset) => asset.id !== assetId));
  };

  const formatDate = (date: Date | null) => {
    if (!date) {
      return "-";
    }
    return formatRelative(date, new Date());
  };

  return (
    <Layout breadcrumbs={[{ label: standardAgentName, current: true }]}>
      <div className="flex-1 bg-background">
        <div className="min-h-[calc(100vh-128px)] px-6 py-6 flex flex-col items-center justify-center">
          <div className="w-full max-w-[768px]">
            <div className="flex items-center justify-center gap-3 text-center text-3xl sm:text-4xl font-semibold text-foreground">
              <div className="flex shrink-0 animate-in fade-in zoom-in-50 spin-in-[-180deg] slide-in-from-left-16 duration-700 ease-out motion-reduce:animate-none">
                <img
                  src={logoBlack}
                  alt="Forgepoint"
                  className="size-9 sm:size-10 shrink-0 dark:hidden"
                />
                <img
                  src={logoWhite}
                  alt="Forgepoint"
                  className="hidden size-9 sm:size-10 shrink-0 dark:block"
                />
              </div>
              <span className="animate-in fade-in duration-500 delay-500 fill-mode-backwards motion-reduce:animate-none">
                How can I help you today?
              </span>
            </div>
            <Card
              className={cn(
                "mt-6 rounded-lg border bg-background transition-all duration-200 relative overflow-hidden",
                isDragOver
                  ? "border-primary border-dashed ring-2 ring-primary/20"
                  : "border-border",
              )}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer.types.includes("Files")) {
                  setIsDragOver(true);
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setIsDragOver(false);
                }
              }}
              onDrop={handleDrop}
            >
              {isDragOver && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/5 pointer-events-none animate-in fade-in duration-150">
                  <div className="flex flex-col items-center gap-2 text-primary">
                    <Upload className="h-6 w-6" />
                    <span className="text-sm font-medium">
                      Drop files to attach
                    </span>
                  </div>
                </div>
              )}
              <CardContent className="p-4">
                <div className="mt-2">
                  {attachedAssets.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {attachedAssets.map((asset) => (
                        <div
                          key={asset.id}
                          className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-sm"
                        >
                          <button
                            type="button"
                            className="max-w-[200px] truncate hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            title={asset.fileName}
                            onClick={() => setPreviewAsset(asset)}
                          >
                            {asset.fileName}
                          </button>
                          <button
                            onClick={() => handleRemoveAsset(asset.id)}
                            className="hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm transition-colors"
                            aria-label={`Remove ${asset.fileName}`}
                            disabled={isSending}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onSend={handleSendMessage}
                      placeholder="Ask a question about your opportunities, quotes, or customers..."
                      disabled={isSending || isUploadingFile}
                      className="overflow-hidden border-0 text-base shadow-none focus-visible:ring-0"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      aria-label="Upload file attachment"
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {enableEmailSearch && (
                          <Popover
                            open={isIntegrationsOpen}
                            onOpenChange={setIsIntegrationsOpen}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={isSending}
                                className="shrink-0 rounded-full border"
                              >
                                <Store className="h-4 w-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              side="top"
                              align="start"
                              className="w-80 p-0"
                            >
                              <div className="p-4">
                                <div className="mb-4 flex items-center gap-2">
                                  <h3 className="text-sm font-semibold">
                                    Work With
                                  </h3>
                                  <button
                                    className="ml-auto text-muted-foreground hover:text-foreground"
                                    onClick={() => setIsIntegrationsOpen(false)}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  <button
                                    onClick={handleOutlookIntegrationClick}
                                    disabled={hasOutlookConnection}
                                    className="group flex w-full items-center gap-2 rounded-lg border border-border p-2 transition-all hover:border-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-transparent"
                                  >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background">
                                      <Mail className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex-1 text-left">
                                      <div className="text-sm font-medium">
                                        Microsoft Outlook
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {hasOutlookConnection
                                          ? "Email connected"
                                          : "Connect your email"}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                                      {hasOutlookConnection
                                        ? "Connected"
                                        : "Set up"}
                                    </div>
                                  </button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        <VisibilityToggle
                          visibility={visibility}
                          onToggle={() =>
                            setVisibility((v) =>
                              v === THREAD_VISIBILITY.ORGANIZATION
                                ? THREAD_VISIBILITY.PRIVATE
                                : THREAD_VISIBILITY.ORGANIZATION,
                            )
                          }
                          disabled={isSending}
                        />
                      </div>
                      <div className="flex items-center">
                        <SendButton
                          onClick={handleSendMessage}
                          disabled={
                            !newMessage.trim() || isSending || isUploadingFile
                          }
                          loading={isSending}
                          className="shrink-0 rounded-2"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploadingFile || isSending}
                          className="ml-2 shrink-0"
                        >
                          {isUploadingFile ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Paperclip className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {quickQuestions.map((question) => (
                <Button
                  key={question}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full border-border text-xs"
                  onClick={() => setNewMessage(question)}
                  disabled={isSending}
                >
                  {question}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 text-center text-xs text-muted-foreground">
          Scroll down to see historical conversations.
        </div>

        <div className="px-6 pb-12">
          <div className="mx-auto w-full max-w-4xl space-y-6">
            <TooltipProvider>
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <TabsTrigger value="my-threads">
                          My Conversations
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      Conversations you started
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <TabsTrigger value="org-threads">
                          My Organization&apos;s Conversations
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      All conversations across your organization
                    </TooltipContent>
                  </Tooltip>
                </TabsList>
              </Tabs>
            </TooltipProvider>
            <ThreadSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              isLoading={isLoadingThreads}
            />
            <div>
              {isLoadingThreads || isWaitingForUserThreads ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : sortedThreads.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-muted-foreground">
                      {debouncedSearch.trim()
                        ? "No conversations match your search"
                        : "No conversations yet"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {sortedThreads.map((thread) => (
                    <Card
                      key={thread.id}
                      className="cursor-pointer border-border hover:border-foreground/50 ease-in-out transition-all duration-200"
                      onClick={() => handleThreadClick(thread.id)}
                    >
                      <CardContent className="flex items-center justify-between gap-4 p-4">
                        <div>
                          <div className="text-sm">
                            {thread.name ? (
                              thread.name
                            ) : (
                              <Skeleton className="h-5 w-32" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Last message {formatDate(thread.updatedAt)}
                          </div>
                        </div>
                        {getStatusIcon(thread)}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <StandardAgentThreadView
          threadId={selectedThreadId}
          isOpen={threadViewOpen}
          onClose={() => {
            setSearchParams({});
          }}
          enableTaskDeepLink
        />
      </div>

      <AssetPreviewDialog
        assetId={previewAsset?.id ?? null}
        fileName={previewAsset?.fileName}
        onClose={() => setPreviewAsset(null)}
      />
    </Layout>
  );
}
